import { ref, computed, Ref } from 'vue';
import { ConfigStep } from '@/composables/useIntegrations';

export interface StepStatus {
  key: string;
  accessible: boolean;
  complete: boolean;
  validated: boolean;
}

export function useConfigWizard(
  steps: Ref<ConfigStep[]>,
  savedConfigs: Ref<Map<string, string>>,
) {
  const currentStepIndex = ref(0);

  const currentStep = computed(() => steps.value[currentStepIndex.value] || null);

  const canGoBack = computed(() => currentStepIndex.value > 0);
  const canGoNext = computed(() => currentStepIndex.value < steps.value.length - 1);
  const isFirstStep = computed(() => currentStepIndex.value === 0);
  const isLastStep = computed(() => currentStepIndex.value === steps.value.length - 1);

  const isStepComplete = (stepKey: string): boolean => {
    const step = steps.value.find((s) => s.key === stepKey);
    if (!step) return false;

    return (step.fields || [])
      .filter((f) => f.required)
      .every((f) => {
        const val = savedConfigs.value.get(f.key);
        return val !== undefined && val !== null && val !== '';
      });
  };

  const isStepValidated = (stepKey: string): boolean => {
    const step = steps.value.find((s) => s.key === stepKey);
    if (!step || !step.on_complete) return true;
    return savedConfigs.value.get(`__step:${stepKey}:validated`) === 'true';
  };

  const isStepAccessible = (stepKey: string): boolean => {
    const index = steps.value.findIndex((s) => s.key === stepKey);
    if (index < 0) return false;
    const step = steps.value[index];
    // Explicit dependency wins — supports non-linear flows declared in the
    // manifest (`depends_on`).
    if (step.depends_on) {
      return isStepComplete(step.depends_on) && isStepValidated(step.depends_on);
    }
    // Default: strictly SEQUENTIAL. A step is reachable only once every earlier
    // step is complete AND validated. Without this, steps that declare no
    // `depends_on` were all reachable, letting the user skip ahead via the
    // stepper bullets (T2).
    for (let i = 0; i < index; i += 1) {
      if (!isStepComplete(steps.value[i].key) || !isStepValidated(steps.value[i].key)) {
        return false;
      }
    }
    return true;
  };

  const stepStatuses = computed((): StepStatus[] => steps.value.map((step) => ({
    key: step.key,
    accessible: isStepAccessible(step.key),
    complete: isStepComplete(step.key),
    validated: isStepValidated(step.key),
  })));

  const allStepsComplete = computed(() => stepStatuses.value.every((s) => s.complete && s.validated));

  const next = () => {
    if (canGoNext.value) {
      currentStepIndex.value += 1;
    }
  };

  const prev = () => {
    if (canGoBack.value) {
      currentStepIndex.value -= 1;
    }
  };

  const goToStep = (stepKey: string) => {
    const index = steps.value.findIndex((s) => s.key === stepKey);
    if (index >= 0 && isStepAccessible(steps.value[index].key)) {
      currentStepIndex.value = index;
    }
  };

  const goToIndex = (index: number) => {
    if (index >= 0 && index < steps.value.length) {
      if (isStepAccessible(steps.value[index].key)) {
        currentStepIndex.value = index;
      }
    }
  };

  return {
    currentStepIndex,
    currentStep,
    canGoBack,
    canGoNext,
    isFirstStep,
    isLastStep,
    stepStatuses,
    allStepsComplete,
    isStepComplete,
    isStepValidated,
    isStepAccessible,
    next,
    prev,
    goToStep,
    goToIndex,
  };
}
